import { lazy } from 'react';

import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';

import { MainLayout } from '@/widgets/layouts';

import { LandingParamsInterceptor } from '@/features/landingFlow';

import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';

import { BaseLayout, InitAppLayout } from '../layouts';
import { PaymentDelayLayout } from '../layouts/PaymentDelayLayout';
import { HomeRedirect } from './HomeRedirect';
import { LegacyRouteBackwardCompatibility } from './LegacyRouteBackwardCompatibility';
import { ProtectedRoute } from './ProtectedRoute';
import { SpaceGuard } from './SpaceGuard';

const ErrorPage = lazy(() => import('@/pages/ErrorPage'));
const HomePage = lazy(() => import('@/pages/HomePage'));
const ProjectPage = lazy(() => import('@/pages/ProjectPage'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));
const AdminPage = lazy(() => import('@/pages/AdminPage'));
const Customers = lazy(() => import('@/pages/AdminPage/components/Content/components/Customers'));
const CustomersFeedback = lazy(() => import('@/pages/AdminPage/components/Content/components/CustomersFeedback'));
const Survey = lazy(() => import('@/pages/AdminPage/components/Content/components/Survey'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const AccountPage = lazy(() => import('@/pages/AccountPage'));
const CheckoutPage = lazy(() => import('@/pages/CheckoutPage'));
const VoicePresetsPage = lazy(() => import('@/pages/VoicePresetsPage'));
const GlossariesPage = lazy(() => import('@/pages/GlossariesPage'));
const FoldersPage = lazy(() => import('@/pages/FoldersPage'));
const TrialSharePage = lazy(() => import('@/pages/TrialSharePage'));

const mainRoutes: RouteObject[] = [
  {
    children: [
      {
        element: <HomePage />,
        index: true,
      },
      {
        element: <VoicePresetsPage />,
        path: PATHS.voices,
      },
      {
        element: <GlossariesPage />,
        path: PATHS.glossaries,
      },
      {
        children: [
          {
            element: <Navigate to="../" replace />,
            index: true,
          },
          {
            element: <FoldersPage />,
            path: ':folderId',
          },
        ],
        path: PATHS.folders,
      },
    ],
  },
];

const projectRoute: RouteObject = {
  children: [
    {
      children: [
        {
          element: <Navigate to="../" replace />,
          index: true,
        },
        {
          element: <ProjectPage />,
          path: ':projectId',
        },
      ],
      path: PATHS.project,
    },
  ],
};

const adminRoutes: RouteObject[] = [
  {
    element: <Navigate to={PATHS.admin.customers} replace />,
    index: true,
  },
  {
    element: <Customers />,
    path: PATHS.admin.customers,
  },
  {
    element: <CustomersFeedback />,
    path: PATHS.admin.feedback,
  },
  {
    element: <Survey />,
    path: PATHS.admin.survey,
  },
];

const legacyRoutes: RouteObject[] = [
  {
    children: [
      {
        children: [
          {
            element: <Navigate to="../" replace />,
            index: true,
          },
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: ':projectId',
          },
        ],
        path: PATHS.project,
      },
      {
        children: [
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: PATHS.voices,
          },
          {
            element: <LegacyRouteBackwardCompatibility />,
            path: PATHS.glossaries,
          },
          {
            children: [
              {
                element: <Navigate to="../" replace />,
                index: true,
              },
              {
                element: <LegacyRouteBackwardCompatibility />,
                path: ':folderId',
              },
            ],
            path: PATHS.folders,
          },
        ],
      },
    ],
  },
];

const routes: RouteObject[] = [
  {
    children: [
      { children: legacyRoutes },
      {
        children: [
          {
            children: mainRoutes,
            element: <MainLayout />,
          },
          projectRoute,
        ],
        Component: SpaceGuard,
        path: `${PATHS.spaces}/:spaceId`,
      },
      {
        element: <HomeRedirect />,
        path: PATHS.homepage,
      },
    ],
  },
  {
    children: adminRoutes,
    element: <AdminPage />,
    path: PATHS.admin.homepage,
  },
];

if (!IS_WHITE_LABEL) {
  routes.push(
    {
      element: <PricingPage />,
      path: PATHS.pricing,
    },
    {
      element: <AccountPage />,
      path: PATHS.account,
    },
    {
      element: <CheckoutPage />,
      path: PATHS.checkout,
    },
  );
}

export const router = createBrowserRouter([
  {
    children: [
      {
        children: [
          {
            children: [
              {
                children: [{ children: routes, element: <BaseLayout /> }],
                element: <InitAppLayout />,
              },
            ],
            element: <PaymentDelayLayout />,
          },
        ],
        element: <ProtectedRoute />,
        errorElement: <ErrorPage />,
      },
      {
        // Публичный маршрут для просмотра проекта без авторизации
        element: <TrialSharePage />,
        path: 'share/:sharing_id',
      },
      {
        element: <AuthPage />,
        path: PATHS.auth,
      },
      {
        element: <NotFoundPage />,
        path: PATHS.any,
      },
    ],
    element: <LandingParamsInterceptor />,
  },
]);
